import React from 'react';
import { Alert, Typography } from '@mui/material';

/**
 * The risk section of an AI call note (23 Sep 2026).
 *
 * Every note made since then says what happened about risk in the contact:
 * disclosed, asked and none disclosed, or not discussed. Two states are set by
 * the system rather than the summary: screen_flagged (the keyword safety screen
 * found critical or high words the summary did not record as risk) and missing
 * (no valid risk section after one retry). Both ask the therapist to review.
 */

const STATES = {
  disclosed: { severity: 'warning', title: 'Risk disclosed in this contact' },
  screen_flagged: { severity: 'error', title: 'Safety screen flag: review the transcript before relying on this note' },
  missing: { severity: 'error', title: 'Risk statement missing: review required' },
  asked_none_disclosed: { severity: 'success', title: 'Asked about risk: none disclosed' },
  not_discussed: { severity: 'info', title: 'Risk not discussed in this contact' }
};

export default function CallRiskSection({ aiSummary }) {
  if (!aiSummary) return null;
  const risks = Array.isArray(aiSummary.risks) ? aiSummary.risks : [];
  const state = STATES[aiSummary.riskStatus];
  const screen = aiSummary.riskScreen || {};
  const screenWords = Array.isArray(screen.matchedKeywords) ? screen.matchedKeywords : [];

  // Notes made before risk statements were required.
  if (!state) {
    return (
      <Alert severity={risks.length ? 'warning' : 'info'} sx={{ mb: 2 }} data-testid="call-risk-section" data-state="legacy">
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {risks.length ? 'Flagged for attention' : 'No risk section'}
        </Typography>
        {risks.map((r, i) => <Typography key={i} variant="body2">• {r}</Typography>)}
        {!risks.length && (
          <Typography variant="body2">
            This note was made before notes had to state what was discussed about risk.
          </Typography>
        )}
      </Alert>
    );
  }

  return (
    <Alert severity={state.severity} sx={{ mb: 2 }} data-testid="call-risk-section" data-state={aiSummary.riskStatus}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{state.title}</Typography>
      {aiSummary.riskStatement && (
        <Typography variant="body2" sx={{ mb: risks.length ? 0.5 : 0 }}>{aiSummary.riskStatement}</Typography>
      )}
      {risks.map((r, i) => <Typography key={i} variant="body2">• {r}</Typography>)}
      {aiSummary.riskStatus !== 'screen_flagged' && screenWords.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          The safety screen also noted: {screenWords.join(', ')} ({screen.flagLevel}).
        </Typography>
      )}
    </Alert>
  );
}
